import { useState, useEffect } from 'react';
import { useAuth } from '@/contexts/AuthContext';
import { api } from '@/lib/api';
import styles from './SettingsPage.module.css';

interface ClaudeStatus {
  rate_limits: {
    haiku: { inputRemaining: number; outputRemaining: number; resetAt: number };
    sonnet: { inputRemaining: number; outputRemaining: number; resetAt: number };
  };
  usage_last_30_days: {
    total_cost: number;
    total_input_tokens: number;
    total_output_tokens: number;
    by_model: Record<string, { cost: number; input_tokens: number; output_tokens: number }>;
  };
}

interface AppConfig {
  auto_archive_weeks: number;
  max_file_size_mb: number;
  allowed_file_types: string[];
}

export function SettingsPage() {
  const { user } = useAuth();
  const [claudeStatus, setClaudeStatus] = useState<ClaudeStatus | null>(null);
  const [config, setConfig] = useState<AppConfig | null>(null);
  const [isLoading, setIsLoading] = useState(true);

  useEffect(() => {
    async function loadData() {
      setIsLoading(true);
      try {
        const [statusResponse, configResponse] = await Promise.all([
          api.get<ClaudeStatus>('/settings/claude/status'),
          api.get<AppConfig>('/settings/config'),
        ]);

        if (statusResponse.success && statusResponse.data) {
          setClaudeStatus(statusResponse.data);
        }
        if (configResponse.success && configResponse.data) {
          setConfig(configResponse.data);
        }
      } catch (err) {
        console.error('Failed to load settings:', err);
      } finally {
        setIsLoading(false);
      }
    }

    loadData();
  }, []);

  const formatNumber = (num: number) => {
    return new Intl.NumberFormat().format(Math.round(num));
  };

  const formatCurrency = (amount: number) => {
    return new Intl.NumberFormat('en-US', {
      style: 'currency',
      currency: 'USD',
    }).format(amount);
  };

  const formatFileTypes = (types: string[]) => {
    return types.map((type) => {
      const ext = type.split('/').pop() || type;
      return ext.toUpperCase();
    });
  };

  return (
    <div className={styles.container}>
      <div className={styles.header}>
        <h1>Settings</h1>
        <p className={styles.subtitle}>System configuration and status</p>
      </div>

      {isLoading ? (
        <div className={styles.loading}>
          <div className="spinner" />
          <span>Loading settings...</span>
        </div>
      ) : (
        <div className={styles.sections}>
          {/* Account Info */}
          <section className={styles.section}>
            <h2>Account</h2>
            <div className={styles.card}>
              <div className={styles.infoRow}>
                <span className={styles.label}>Name</span>
                <span className={styles.value}>{user?.name}</span>
              </div>
              <div className={styles.infoRow}>
                <span className={styles.label}>Email</span>
                <span className={styles.value}>{user?.email}</span>
              </div>
              <div className={styles.infoRow}>
                <span className={styles.label}>Role</span>
                <span className={styles.value}>
                  <span className="badge badge-primary">{user?.role}</span>
                </span>
              </div>
            </div>
          </section>

          {/* Claude API Status */}
          {claudeStatus && (
            <section className={styles.section}>
              <h2>Claude API Status</h2>
              <div className={styles.card}>
                <h3>Rate Limits</h3>
                <div className={styles.rateGrid}>
                  <div className={styles.rateCard}>
                    <h4>Haiku</h4>
                    <div className={styles.rateInfo}>
                      <div>
                        <span className={styles.rateLabel}>Input remaining</span>
                        <span className={styles.rateValue}>
                          {formatNumber(claudeStatus.rate_limits.haiku.inputRemaining)}
                        </span>
                      </div>
                      <div>
                        <span className={styles.rateLabel}>Output remaining</span>
                        <span className={styles.rateValue}>
                          {formatNumber(claudeStatus.rate_limits.haiku.outputRemaining)}
                        </span>
                      </div>
                    </div>
                  </div>
                  <div className={styles.rateCard}>
                    <h4>Sonnet</h4>
                    <div className={styles.rateInfo}>
                      <div>
                        <span className={styles.rateLabel}>Input remaining</span>
                        <span className={styles.rateValue}>
                          {formatNumber(claudeStatus.rate_limits.sonnet.inputRemaining)}
                        </span>
                      </div>
                      <div>
                        <span className={styles.rateLabel}>Output remaining</span>
                        <span className={styles.rateValue}>
                          {formatNumber(claudeStatus.rate_limits.sonnet.outputRemaining)}
                        </span>
                      </div>
                    </div>
                  </div>
                </div>

                <h3 className={styles.mt4}>Usage (Last 30 Days)</h3>
                <div className={styles.usageGrid}>
                  <div className={styles.usageStat}>
                    <span className={styles.usageValue}>
                      {formatCurrency(claudeStatus.usage_last_30_days.total_cost)}
                    </span>
                    <span className={styles.usageLabel}>Total Cost</span>
                  </div>
                  <div className={styles.usageStat}>
                    <span className={styles.usageValue}>
                      {formatNumber(claudeStatus.usage_last_30_days.total_input_tokens)}
                    </span>
                    <span className={styles.usageLabel}>Input Tokens</span>
                  </div>
                  <div className={styles.usageStat}>
                    <span className={styles.usageValue}>
                      {formatNumber(claudeStatus.usage_last_30_days.total_output_tokens)}
                    </span>
                    <span className={styles.usageLabel}>Output Tokens</span>
                  </div>
                </div>
              </div>
            </section>
          )}

          {/* System Configuration */}
          {config && (
            <section className={styles.section}>
              <h2>System Configuration</h2>
              <div className={styles.card}>
                <div className={styles.infoRow}>
                  <span className={styles.label}>Auto-archive after</span>
                  <span className={styles.value}>{config.auto_archive_weeks} weeks</span>
                </div>
                <div className={styles.infoRow}>
                  <span className={styles.label}>Max file size</span>
                  <span className={styles.value}>{config.max_file_size_mb} MB</span>
                </div>
                <div className={styles.infoRow}>
                  <span className={styles.label}>Supported file types</span>
                  <span className={styles.value}>
                    <div className={styles.fileTypes}>
                      {formatFileTypes(config.allowed_file_types).map((type, i) => (
                        <span key={i} className={styles.fileType}>
                          {type}
                        </span>
                      ))}
                    </div>
                  </span>
                </div>
              </div>
            </section>
          )}

          {/* About */}
          <section className={styles.section}>
            <h2>About</h2>
            <div className={styles.card}>
              <div className={styles.infoRow}>
                <span className={styles.label}>Version</span>
                <span className={styles.value}>1.0.0</span>
              </div>
              <div className={styles.infoRow}>
                <span className={styles.label}>Platform</span>
                <span className={styles.value}>Cloudflare Workers</span>
              </div>
              <div className={styles.infoRow}>
                <span className={styles.label}>AI Model</span>
                <span className={styles.value}>Claude (Haiku & Sonnet)</span>
              </div>
            </div>
          </section>
        </div>
      )}
    </div>
  );
}
